import type { LoomAuthoringDocument, LoomAxis, LoomSceneNode } from "./loomAuthoringModel";

export interface LoomMirrorAttribute {
  axis: LoomAxis;
  frame: "world";
  partnerNodeId: string;
  planeOffsetM: number;
  sourceNodeId: string;
}

type LoomNodeAttachment =
  | { kind: "new-root" }
  | { kind: "replace-all-occurrences"; targetNodeId: string };

function nodeInputs(node: LoomSceneNode): readonly string[] {
  if (node.type === "transform") return [node.inputNodeId];
  return node.type === "boolean" ? node.inputNodeIds : [];
}

function replaceInput(node: LoomSceneNode, targetNodeId: string, replacementNodeId: string): LoomSceneNode {
  if (node.type === "transform" && node.inputNodeId === targetNodeId) {
    return { ...node, inputNodeId: replacementNodeId };
  }
  if (node.type === "boolean" && node.inputNodeIds.includes(targetNodeId)) {
    return { ...node, inputNodeIds: node.inputNodeIds.map((id) => id === targetNodeId ? replacementNodeId : id) };
  }
  return node;
}

function rootedNodeIds(document: LoomAuthoringDocument): Set<string> {
  const byId = new Map(document.nodes.map((node) => [node.id, node]));
  const reached = new Set<string>();
  const visit = (nodeId: string): void => {
    if (reached.has(nodeId)) return;
    reached.add(nodeId);
    const node = byId.get(nodeId);
    if (node) nodeInputs(node).forEach(visit);
  };
  document.rootNodeIds.forEach(visit);
  return reached;
}

/** Project distinct graph entities as one user-facing source attribute. */
export function deriveLoomMirrorAttributes(
  document: LoomAuthoringDocument,
  sourceNodeId?: string,
): LoomMirrorAttribute[] {
  return document.nodes.flatMap((node) => {
    if (node.type !== "transform" || node.operation.transform !== "mirror") return [];
    if (sourceNodeId !== undefined && node.inputNodeId !== sourceNodeId) return [];
    return [{
      axis: node.operation.axis,
      frame: "world" as const,
      partnerNodeId: node.id,
      planeOffsetM: node.operation.planeOffsetM,
      sourceNodeId: node.inputNodeId,
    }];
  });
}

/** Atomically detach one reflected entity without leaving a dangling half-edit. */
export function detachLoomMirrorPartner(
  document: LoomAuthoringDocument,
  sourceNodeId: string,
  partnerNodeId: string,
): { nodes: LoomSceneNode[]; rootNodeIds: string[] } {
  const partner = document.nodes.find((node) => node.id === partnerNodeId);
  if (!partner || partner.type !== "transform" || partner.operation.transform !== "mirror" || partner.inputNodeId !== sourceNodeId) {
    throw new Error(`mirror partner ${partnerNodeId} is not linked to source ${sourceNodeId}`);
  }
  const reached = rootedNodeIds(document);
  const unions = document.nodes.filter((node): node is Extract<LoomSceneNode, { type: "boolean" }> => (
    node.type === "boolean" && node.operation === "union" && reached.has(node.id)
    && node.inputNodeIds.includes(sourceNodeId) && node.inputNodeIds.includes(partnerNodeId)
  ));
  if (unions.length !== 1) throw new Error("mirror partner removal requires exactly one rooted source/partner union");
  const union = unions[0]!;
  const externalReferences = document.nodes.filter((node) => node.id !== union.id && nodeInputs(node).includes(partnerNodeId));
  if (externalReferences.length || document.rootNodeIds.includes(partnerNodeId)) {
    throw new Error(`mirror partner ${partnerNodeId} is referenced outside its source union`);
  }
  const remainingInputs = union.inputNodeIds.filter((nodeId) => nodeId !== partnerNodeId);
  if (remainingInputs.length === 1) {
    const replacementNodeId = remainingInputs[0]!;
    return {
      nodes: document.nodes
        .filter((node) => node.id !== partnerNodeId && node.id !== union.id)
        .map((node) => replaceInput(node, union.id, replacementNodeId)),
      rootNodeIds: document.rootNodeIds.map((nodeId) => nodeId === union.id ? replacementNodeId : nodeId),
    };
  }
  return {
    nodes: document.nodes
      .filter((node) => node.id !== partnerNodeId)
      .map((node) => node.id === union.id ? { ...union, inputNodeIds: remainingInputs } : node),
    rootNodeIds: [...document.rootNodeIds],
  };
}

export function attachLoomNode(
  document: LoomAuthoringDocument,
  node: LoomSceneNode,
  attachment: LoomNodeAttachment,
): { nodes: LoomSceneNode[]; rootNodeIds: string[] } {
  if (document.nodes.some(({ id }) => id === node.id)) throw new Error(`node ${node.id} already exists`);
  if (node.type === "transform" && node.operation.transform === "mirror") {
    throw new Error("mirrored partners require mirror.partner.create for atomic attachment");
  }
  if (attachment.kind === "new-root") {
    return { nodes: [...document.nodes, node], rootNodeIds: [...document.rootNodeIds, node.id] };
  }
  const targetNodeId = attachment.targetNodeId;
  if (!document.nodes.some(({ id }) => id === targetNodeId)) throw new Error(`node ${targetNodeId} does not exist`);
  if (!nodeInputs(node).includes(targetNodeId)) throw new Error("attached node must directly consume its replacement target");
  const byId = new Map(document.nodes.map((existing) => [existing.id, existing])); const reachable = new Set<string>();
  const visit = (id: string): void => { if (reachable.has(id)) return; reachable.add(id); nodeInputs(byId.get(id)!).forEach(visit); };
  document.rootNodeIds.forEach(visit);
  if (!reachable.has(targetNodeId)) throw new Error(`node ${targetNodeId} is not connected to a Pattern output`);
  return {
    nodes: [...document.nodes.map((existing) => replaceInput(existing, targetNodeId, node.id)), node],
    rootNodeIds: document.rootNodeIds.map((id) => id === targetNodeId ? node.id : id),
  };
}

/** Keep every authored reflection as a distinct source + partner in a rooted union. */
export function requireAttachedLoomMirrorPartners(document: LoomAuthoringDocument): LoomAuthoringDocument {
  const byId = new Map(document.nodes.map((node) => [node.id, node]));
  const reachable = new Set<string>();
  const visit = (nodeId: string): void => {
    if (reachable.has(nodeId)) return;
    reachable.add(nodeId);
    const node = byId.get(nodeId);
    if (node) nodeInputs(node).forEach(visit);
  };
  document.rootNodeIds.forEach(visit);

  for (const partner of document.nodes) {
    if (partner.type !== "transform" || partner.operation.transform !== "mirror") continue;
    const attached = document.nodes.some((node) => (
      node.type === "boolean"
      && node.operation === "union"
      && reachable.has(node.id)
      && node.inputNodeIds.includes(partner.inputNodeId)
      && node.inputNodeIds.includes(partner.id)
    ));
    if (!reachable.has(partner.id) || !attached) {
      throw new Error(`mirrored partner ${partner.id} must remain linked with source ${partner.inputNodeId} in one rooted union`);
    }
  }
  return document;
}
